import { toDecimal, roundCents } from './internal/decimal-utils.js';
import { CoreError, MissingMoistureError, MoistureOutOfBandError } from './errors.js';
function tonnesOf(netKg) {
    return toDecimal(netKg).div(1000);
}
function requirePrice(priceCentsPerTonne) {
    if (priceCentsPerTonne === undefined) {
        throw new CoreError('MISSING_PRICE', 'priceCentsPerTonne is required for this pricing basis');
    }
    return priceCentsPerTonne;
}
function requireMc(mcPct) {
    if (mcPct === undefined || mcPct === null) {
        throw new MissingMoistureError();
    }
    return mcPct;
}
/** Shared band-price payable math (§7 MOISTURE_BAND: `payable = W/1000 × P_band`) —
 * used by BOTH the normal in-band branch below and `moistureBandOverridePayableCents`
 * (the ADMIN out-of-band override path, amendment R8) so the two paths can never
 * double-round or diverge on rounding policy. */
function payableAtTonnePrice(netKg, priceCentsPerTonne) {
    return roundCents(tonnesOf(netKg).times(toDecimal(priceCentsPerTonne)));
}
/**
 * Selects the band containing `mc`. Bands are inclusive on BOTH ends of their own
 * range, and are checked in ascending `mcMin` order — so a reading that sits exactly
 * on a shared boundary (e.g. band A = [0,20], band B = [20,40], reading = 20) resolves
 * to the FIRST (lower) band whose `mcMax` equals the reading, per spec: "a reading
 * equal to a boundary belongs to the band whose mcMax equals it".
 */
function findBand(bands, mc) {
    const sorted = [...bands].sort((a, b) => a.mcMin - b.mcMin);
    return sorted.find((band) => mc >= band.mcMin && mc <= band.mcMax);
}
export function payableCents(input) {
    const { basis, netKg, mcPct, priceCentsPerTonne, bands } = input;
    switch (basis) {
        case 'FLAT_WET': {
            const price = requirePrice(priceCentsPerTonne);
            const payable = roundCents(tonnesOf(netKg).times(toDecimal(price)));
            return { payableCents: payable, usedPriceCents: price };
        }
        case 'BDT': {
            const price = requirePrice(priceCentsPerTonne);
            const mc = requireMc(mcPct);
            // Spec §7: D = W×(1−m/100)/1000 at FULL precision — money rounds half-up to
            // whole cents exactly once, at the payable boundary. Routing through dryKg()
            // would quantize the dry mass to 3 dp first and double-round the payable
            // (Wave 2 gate 2026-07-07). dryKg() remains the form for stock-ledger masses.
            const dryTonnes = toDecimal(netKg)
                .times(toDecimal(1).minus(toDecimal(mc).div(100)))
                .div(1000);
            const payable = roundCents(dryTonnes.times(toDecimal(price)));
            return { payableCents: payable, usedPriceCents: price };
        }
        case 'MOISTURE_BAND': {
            const mc = requireMc(mcPct);
            if (!bands || bands.length === 0) {
                throw new CoreError('MISSING_BANDS', 'bands are required for MOISTURE_BAND pricing');
            }
            const mcNumber = toDecimal(mc).toNumber();
            const band = findBand(bands, mcNumber);
            if (!band) {
                throw new MoistureOutOfBandError(mcNumber);
            }
            const payable = payableAtTonnePrice(netKg, band.priceCents);
            return { payableCents: payable, usedPriceCents: band.priceCents };
        }
        default: {
            // Exhaustiveness guard — enums.ts is the single source of PricingBasis values.
            throw new CoreError('UNKNOWN_PRICING_BASIS', `Unknown pricing basis: ${String(basis)}`);
        }
    }
}
/**
 * Payable for an EACH-priced (count) purchase order line — packaging/consumables
 * bought and priced per unit rather than per tonne (§-EACH; the moisture-basis
 * `payableCents` above does not apply, since a count line has no moisture basis).
 * `payable = qty * priceCentsPerUnit`, rounded the SAME way every other money
 * boundary in this codebase rounds (`roundCents` — half-up to whole cents,
 * exactly once, at this boundary). No positivity/sign validation here — `qty`
 * and `priceCentsPerUnit` are validated at the schema layer (`countLineSchema`,
 * schemas/purchasing.ts); this function is pure arithmetic on whatever it is
 * given, mirroring `payableCents`'s own division of labour.
 */
export function eachPayableCents(qty, priceCentsPerUnit) {
    return roundCents(toDecimal(qty).times(toDecimal(priceCentsPerUnit)));
}
// ---------------------------------------------------------------------------
// MOISTURE_BAND out-of-band ADMIN override (spec §7 amendment R8, ratified
// 2026-07-07): "the MOISTURE_BAND out-of-band override is ADMIN-only ... When
// overridden, payable is computed at the price of the nearest band edge (the
// contract band boundary mc_min/mc_max closest to the measured m), not an
// extrapolated or negotiated figure — deterministic and auditable." The
// ADMIN-only gate, mandatory-reason persistence, and hard-block-when-missing
// behaviour live at the API layer (services/api — this module has no actor/role
// concept); this file owns only the deterministic PRICE the override applies.
// ---------------------------------------------------------------------------
/**
 * Deterministic ADMIN-override price for a MOISTURE_BAND reading that fell
 * outside every configured band. Scans EVERY band's own two boundaries
 * (`mcMin` AND `mcMax` — not just the two global extremes, since a contract's
 * bands may have gaps between them) and returns the price of whichever band
 * owns the boundary closest to `mcPct`.
 *
 * Tie-break rule (documented here, not left to array/iteration order): when two
 * boundaries — necessarily from two different bands, since a single band's own
 * mcMin/mcMax are equidistant from its interior only at its midpoint, never from
 * a point outside the band — are EQUALLY close to `mcPct`, the LOWER price wins.
 * This is conservative for the buyer (Zebra pays less, never more, when the
 * measurement sits exactly between two bands) and, being a fixed rule rather
 * than an arbitrary pick, keeps the result deterministic and auditable per the
 * ruling.
 *
 * Distances are computed and compared with decimal.js — the same machinery as
 * every other piece of math in this file — NOT native Number arithmetic (audit
 * fix round 2026-07-07): IEEE-754 subtraction leaves artifacts on plain decimal
 * inputs (e.g. `0.3 - 0.2 === 0.09999999999999998` while `0.2 - 0.1 === 0.1`),
 * so a Number-based comparison can call two genuinely-equidistant edges unequal
 * and silently skip the tie-break above. The result would still have been one
 * of the two legitimate edge prices (no money invention), but the documented
 * determinism rule would be violated. `toDecimal` converts each edge via
 * `Number.prototype.toString()` — the shortest round-trip decimal
 * representation — so a band edge stored as the JS number 20.05 becomes the
 * exact Decimal 20.05, and the subtraction/comparison below are exact at the
 * inputs' stored decimal precision.
 *
 * Pure and total over any non-empty `bands` array — callers (the API's POST
 * path) are expected to invoke this ONLY after `payableCents` has already
 * thrown `MoistureOutOfBandError` for the same reading, but nothing here
 * depends on that; it will happily return the nearest-edge price for an
 * in-band reading too (there is just no reason to call it for one).
 */
export function nearestBandEdgePrice(bands, mcPct) {
    if (!bands || bands.length === 0) {
        throw new CoreError('MISSING_BANDS', 'bands are required for MOISTURE_BAND pricing');
    }
    const mc = toDecimal(mcPct);
    let bestDistance;
    let bestPrice;
    for (const band of bands) {
        for (const edge of [band.mcMin, band.mcMax]) {
            const distance = mc.minus(toDecimal(edge)).abs();
            const comparison = bestDistance === undefined ? -1 : distance.comparedTo(bestDistance);
            const isCloser = comparison < 0;
            const isTieAtLowerPrice = comparison === 0 && bestPrice !== undefined && band.priceCents < bestPrice;
            if (isCloser || isTieAtLowerPrice) {
                bestDistance = distance;
                bestPrice = band.priceCents;
            }
        }
    }
    // Unreachable given the length check above (every band contributes at least
    // one finite-distance edge), but keeps the return type non-optional without
    // a cast.
    if (bestPrice === undefined) {
        throw new CoreError('MISSING_BANDS', 'bands are required for MOISTURE_BAND pricing');
    }
    return bestPrice;
}
/**
 * ADMIN-override payable for a MOISTURE_BAND reading that fell outside every
 * band (spec §7 amendment R8) — prices at `nearestBandEdgePrice`'s deterministic
 * nearest-edge price, then applies the EXACT SAME rounding as the normal in-band
 * path (`payableAtTonnePrice`, shared by both — no separate/duplicated rounding
 * rule for the override branch).
 */
export function moistureBandOverridePayableCents(input) {
    const mcNumber = toDecimal(input.mcPct).toNumber();
    const price = nearestBandEdgePrice(input.bands, mcNumber);
    const payable = payableAtTonnePrice(input.netKg, price);
    return { payableCents: payable, usedPriceCents: price };
}
//# sourceMappingURL=payable.js.map